import {
  MAXIMUM_VIDEO_SELECTIONS,
  MetricRegistry,
  videoLabel,
  type ComparisonResponse,
  type ReportMetricId,
  type VideoMetricRow,
  type VideoSelection,
} from "@meta-video/contracts";
import {
  Alert,
  Button,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import {
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type FocusEvent,
} from "react";

import { formatDuration, formatMetricValue, NoDataCell } from "../report/format.js";
import {
  isReportMetricColumnId,
  isVideoCoverageMetric,
  reportColumnIds,
  reportColumnLabel,
} from "../report/columns.js";
import type { VideoApi } from "../video/api.js";
import { StatusBadge } from "../video/StatusBadge.js";
import { VideoPlayer } from "../video/VideoPlayer.js";
import { ShareLinkButton } from "../workspace/ShareLinkButton.js";

type MetricDirection = "higher" | "lower" | "neutral";

const COMPARISON_METRIC_DIRECTIONS = {
  addToCart: "neutral",
  aov: "higher",
  clicks: "neutral",
  cpa: "lower",
  cpc: "lower",
  cpm: "lower",
  ctr: "higher",
  cvr: "higher",
  holdRate: "higher",
  hookRate: "higher",
  impressions: "neutral",
  outboundClicks: "neutral",
  outboundCtr: "higher",
  purchases: "neutral",
  revenue: "neutral",
  roas: "higher",
  spend: "neutral",
  uniqueClicks: "neutral",
} as const satisfies Readonly<Record<ReportMetricId, MetricDirection>>;

const COMPARISON_BAR_METRICS = [
  "hookRate",
  "holdRate",
  "roas",
] as const satisfies readonly ReportMetricId[];

const COMPARISON_MATRIX_METRICS = reportColumnIds.filter(isReportMetricColumnId);

const COMPARISON_SERIES_COLORS = [
  "#1971c2",
  "#2f9e44",
  "#e67700",
  "#7048e8",
] as const;

const COMPARISON_CURRENCY_METRICS = new Set<ReportMetricId>([
  "aov",
  "cpa",
  "cpc",
  "cpm",
]);

const EMPTY_SLOTS = Array.from({ length: MAXIMUM_VIDEO_SELECTIONS }, (_, index) => index);

export interface ComparisonViewProps {
  readonly api?: VideoApi;
  readonly disabled?: boolean;
  readonly error: unknown;
  readonly errorDescription?: string;
  readonly errorTitle?: string;
  readonly isLoading: boolean;
  readonly isUpdating?: boolean;
  readonly onAccessChanged?: () => void;
  readonly onAddVideo?: () => void;
  readonly onClear?: () => void;
  readonly onClose: () => void;
  readonly onSessionExpired?: () => void;
  readonly onRetry?: () => void;
  readonly opened: boolean;
  readonly outOfSync: boolean;
  readonly outOfSyncDescription?: string;
  readonly outOfSyncTitle?: string;
  readonly response?: ComparisonResponse;
  readonly retryLabel?: string;
  readonly selections: readonly VideoSelection[];
  readonly showingPrevious?: boolean;
}

function selectionKey(selection: VideoSelection): string {
  return `${selection.brandId}\0${selection.videoId}`;
}

function videoMetricsCovered(
  row: VideoMetricRow,
  threshold: number,
): boolean {
  return row.videoCoverageRate !== null && row.videoCoverageRate >= threshold;
}

function comparableMetricValue(
  row: VideoMetricRow | undefined,
  metric: ReportMetricId,
  threshold: number,
): number | null {
  if (row === undefined) return null;
  if (
    isVideoCoverageMetric(metric)
    && !videoMetricsCovered(row, threshold)
  ) return null;
  return row[metric];
}

function bestMetricValue(
  rows: readonly (VideoMetricRow | undefined)[],
  metric: ReportMetricId,
  threshold: number,
): number | null {
  const direction = COMPARISON_METRIC_DIRECTIONS[metric];
  if (direction === "neutral") return null;
  const candidates = rows.flatMap((row) => {
    const value = comparableMetricValue(row, metric, threshold);
    return row === undefined || value === null ? [] : [{ currency: row.currency, value }];
  });
  if (COMPARISON_CURRENCY_METRICS.has(metric)) {
    const currencies = new Set(candidates.map(({ currency }) => currency));
    const [currency] = currencies;
    if (
      currencies.size !== 1
      || currency === undefined
      || !/^[A-Z]{3}$/u.test(currency)
    ) return null;
  }
  const values = candidates.map(({ value }) => value);
  if (values.length === 0) return null;
  return direction === "higher" ? Math.max(...values) : Math.min(...values);
}

function rowLabel(
  row: VideoMetricRow | undefined,
  selection: VideoSelection,
): string {
  return row === undefined ? `Video ${selection.videoId}` : videoLabel(row).primary;
}

export function ComparisonView({
  api,
  disabled = false,
  error,
  errorDescription,
  errorTitle = "Video comparison could not be loaded",
  isLoading,
  isUpdating = false,
  onAccessChanged,
  onAddVideo,
  onClear,
  onClose,
  onSessionExpired,
  onRetry,
  opened,
  outOfSync,
  outOfSyncDescription = "The comparison and report resolved against different snapshots, so mismatched metrics are hidden.",
  outOfSyncTitle = "Video comparison is out of sync",
  response,
  retryLabel = "Reload comparison",
  selections,
  showingPrevious = false,
}: ComparisonViewProps) {
  const comparisonViewportRef = useRef<HTMLDivElement>(null);
  const identityTrackRef = useRef<HTMLDivElement>(null);
  const showResponse = response !== undefined
    && ((!error && !outOfSync) || showingPrevious);
  const syncIdentityTrack = useCallback(() => {
    if (comparisonViewportRef.current === null || identityTrackRef.current === null) return;
    identityTrackRef.current.style.setProperty(
      "--comparison-scroll-left",
      `${comparisonViewportRef.current.scrollLeft}px`,
    );
  }, []);
  useEffect(() => {
    const viewport = comparisonViewportRef.current;
    if (viewport === null) return;
    syncIdentityTrack();
    viewport.addEventListener("scroll", syncIdentityTrack, { passive: true });
    return () => viewport.removeEventListener("scroll", syncIdentityTrack);
  }, [opened, response, showResponse, syncIdentityTrack]);

  if (!opened) return null;

  const rowsBySelection = new Map(
    response?.rows.map((row) => [selectionKey(row), row]) ?? [],
  );
  const orderedRows = selections.map((selection) =>
    rowsBySelection.get(selectionKey(selection)));
  const emptySlotCount = Math.max(0, MAXIMUM_VIDEO_SELECTIONS - selections.length);
  const belowCoverageCount = response === undefined ? 0 : orderedRows.filter(
    (row) => row !== undefined && !videoMetricsCovered(
      row,
      response.coverage.videoMetricThreshold,
    ),
  ).length;
  const gridStyle = {
    "--comparison-column-count": MAXIMUM_VIDEO_SELECTIONS,
  } as CSSProperties;

  const revealFocusedIdentity = (event: FocusEvent<HTMLDivElement>) => {
    const viewport = comparisonViewportRef.current;
    const target = event.target;
    if (viewport === null || !(target instanceof Element)) return;
    const identity = target.closest<HTMLElement>(".comparison-workspace__identity");
    if (identity === null) return;
    const corner = event.currentTarget.parentElement?.querySelector<HTMLElement>(
      ".comparison-workspace__identity-corner",
    );
    const visibleStart = viewport.scrollLeft + (corner?.offsetWidth ?? 0);
    const visibleEnd = viewport.scrollLeft + viewport.clientWidth;
    const identityStart = identity.offsetLeft;
    const identityEnd = identityStart + identity.offsetWidth;
    const left = identityStart < visibleStart
      ? Math.max(0, identityStart - (corner?.offsetWidth ?? 0))
      : identityEnd > visibleEnd
        ? identityEnd - viewport.clientWidth
        : null;
    if (left !== null) viewport.scrollTo({ behavior: "auto", left });
  };

  return (
    <section
      aria-busy={isLoading || isUpdating}
      aria-label="Comparison workspace"
      className={`comparison-workspace${disabled ? " comparison-workspace--updating" : ""}`}
      role="region"
      style={gridStyle}
    >
      <div className="comparison-workspace__sticky-stack" data-testid="comparison-sticky-stack">
        <Group className="comparison-workspace__actions" justify="space-between" wrap="wrap">
          <Group gap="sm" wrap="wrap">
            <Button autoFocus onClick={onClose} size="compact-sm" variant="default">
              Back to report
            </Button>
            <h1>Comparing {selections.length} videos</h1>
          </Group>
          <Group gap="xs" wrap="wrap">
            <ShareLinkButton analyticsSurface="comparison" compactLabel="Share" label="Share comparison" />
            <Button onClick={onClear} size="compact-sm" variant="default">
              Clear selection
            </Button>
          </Group>
        </Group>
        {showResponse ? (
          <div className="comparison-workspace__identity-clip">
            <div
              className="comparison-workspace__identity-track"
              data-testid="comparison-identity-track"
              inert={disabled ? true : undefined}
              onFocusCapture={revealFocusedIdentity}
              ref={identityTrackRef}
            >
              <div aria-hidden="true" className="comparison-workspace__identity-spacer" />
              {selections.map((selection, index) => (
                <ComparisonIdentity
                  api={api}
                  key={selectionKey(selection)}
                  onAccessChanged={onAccessChanged}
                  onSessionExpired={onSessionExpired}
                  row={orderedRows[index]}
                  selection={selection}
                />
              ))}
              {EMPTY_SLOTS.slice(0, emptySlotCount).map((slot) => (
                <div className="comparison-workspace__identity comparison-workspace__identity--empty" key={`empty-${slot}`}>
                  <Button onClick={onAddVideo} size="compact-sm" variant="subtle">
                    Add video
                  </Button>
                </div>
              ))}
            </div>
            <div aria-hidden="true" className="comparison-workspace__identity-corner" />
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div aria-live="polite" className="report-loading" role="status">
          <Loader size="sm" /> Loading comparison…
        </div>
      ) : null}
      {isUpdating && response ? (
        <div aria-label="Updating comparison" aria-live="polite" className="report-updating" role="status">
          <Loader size="xs" /> Updating comparison… previous results remain visible.
        </div>
      ) : null}
      {showingPrevious && response ? (
        <Alert
          aria-label="Previous comparison remains visible"
          color="blue"
          role="status"
          title="Previous comparison remains visible"
        >
          This exact prior report snapshot remains visible but inactive until a coherent replacement is ready.
        </Alert>
      ) : null}
      {error ? (
        <Alert color="red" role="alert" title={errorTitle}>
          <Stack gap="xs">
            <Text size="sm">
              {errorDescription ?? (response
                ? "The replacement comparison failed. The previous matching snapshot remains visible but inactive."
                : "No partial results are shown. Return to the report or try again.")}
            </Text>
            {onRetry ? (
              <Button onClick={onRetry} size="compact-sm" variant="light">
                {retryLabel}
              </Button>
            ) : null}
          </Stack>
        </Alert>
      ) : null}
      {outOfSync ? (
        <Alert color="red" role="alert" title={outOfSyncTitle}>
          <Stack gap="xs">
            <Text size="sm">{outOfSyncDescription}</Text>
            {onRetry ? (
              <Button onClick={onRetry} size="compact-sm" variant="light">
                {retryLabel}
              </Button>
            ) : null}
          </Stack>
        </Alert>
      ) : null}

      {showResponse ? (
        <Stack gap="sm">
          {response.cacheStatus === "stale" ? (
            <Alert
              aria-label="Cached comparison may be out of date"
              color="orange"
              role="alert"
              title="Cached comparison may be out of date"
            >
              Freshness checks are unavailable, so this complete cached comparison may not include the latest data.
            </Alert>
          ) : null}
          {belowCoverageCount > 0 ? (
            <Alert color="orange" role="alert" title="Some video metrics are hidden">
              {belowCoverageCount} {belowCoverageCount === 1 ? "video is" : "videos are"} below the {Math.round(response.coverage.videoMetricThreshold * 100)}% coverage threshold. Hook and hold rates are hidden for {belowCoverageCount === 1 ? "it" : "them"}.
            </Alert>
          ) : null}
          <div
            className="comparison-workspace__plane"
            data-testid="comparison-analytics-plane"
            inert={disabled ? true : undefined}
          >
            <ComparisonBarChart
              response={response}
              rows={orderedRows}
              selections={selections}
            />
            <ScrollArea
              className="comparison-workspace__scroll"
              classNames={{ viewport: "comparison-workspace__scroll-viewport" }}
              offsetScrollbars="x"
              scrollbars="x"
              type="auto"
              viewportRef={comparisonViewportRef}
              viewportProps={{
                "aria-disabled": disabled ? "true" : undefined,
                "aria-label": "Side-by-side video comparison",
                role: "region",
                tabIndex: 0,
              }}
            >
              <ComparisonMetricMatrix
                response={response}
                rows={orderedRows}
                selections={selections}
              />
            </ScrollArea>
          </div>
          <Text c="dimmed" size="xs">
            Best markers compare rates and efficiency; spend, volume, and duration are neutral.
          </Text>
        </Stack>
      ) : null}
    </section>
  );
}

function ComparisonIdentity({
  api,
  onAccessChanged,
  onSessionExpired,
  row,
  selection,
}: {
  readonly api?: VideoApi;
  readonly onAccessChanged?: () => void;
  readonly onSessionExpired?: () => void;
  readonly row: VideoMetricRow | undefined;
  readonly selection: VideoSelection;
}) {
  const label = rowLabel(row, selection);
  return (
    <article
      aria-label={`Comparison card: ${label}`}
      className="comparison-workspace__identity comparison-card"
      data-testid="comparison-card"
    >
      <Stack gap="xs">
        <div className="comparison-workspace__identity-label">
          <h2 title={label}>{label}</h2>
          <Text c="dimmed" size="xs">
            {row === undefined
              ? `Brand ${selection.brandId}`
              : videoLabel(row).secondary}
          </Text>
        </div>
        {row === undefined ? (
          <Alert color="orange">
            This selected video was not returned for the current filters and access scope.
          </Alert>
        ) : row.playStatus === "hosted" ? (
          <VideoPlayer
            api={api}
            autoPrepare
            key={selectionKey(selection)}
            label={label}
            onAccessChanged={onAccessChanged}
            onSessionExpired={onSessionExpired}
            selection={selection}
            surface="comparison"
          />
        ) : <StatusBadge status={row.playStatus} />}
      </Stack>
    </article>
  );
}

function ComparisonBarChart({
  response,
  rows,
  selections,
}: {
  readonly response: ComparisonResponse;
  readonly rows: readonly (VideoMetricRow | undefined)[];
  readonly selections: readonly VideoSelection[];
}) {
  return (
    <section
      aria-label="Hook, Hold and ROAS comparison chart"
      className="comparison-workspace__chart"
      data-orientation="vertical"
      role="region"
    >
      <div className="comparison-workspace__chart-header">
        <div>
          <h2>Hook, Hold and ROAS</h2>
          <Text c="dimmed" size="xs">Each metric scales to its highest visible video.</Text>
        </div>
        <ul aria-label="Video color legend" className="comparison-workspace__chart-legend">
          {selections.map((selection, index) => {
            const label = rowLabel(rows[index], selection);
            return (
              <li aria-label={label} key={selectionKey(selection)} title={label}>
                <span
                  aria-hidden="true"
                  className="comparison-workspace__chart-swatch"
                  style={{ "--comparison-series-color": COMPARISON_SERIES_COLORS[index] } as CSSProperties}
                />
                <span>{label}</span>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="comparison-workspace__chart-groups">
        {COMPARISON_BAR_METRICS.map((metric) => {
          const values = rows.map((row) => comparableMetricValue(
            row,
            metric,
            response.coverage.videoMetricThreshold,
          ));
          const maximum = Math.max(
            0,
            ...values.filter((value): value is number => value !== null),
          );
          const metricLabel = reportColumnLabel(metric);
          return (
            <div
              aria-label={`${metricLabel} bars`}
              className="comparison-workspace__chart-group"
              key={metric}
              role="group"
            >
              <h3>{metricLabel}</h3>
              <div className="comparison-workspace__chart-bars">
                {selections.map((selection, index) => {
                  const row = rows[index];
                  const value = values[index] ?? null;
                  const label = rowLabel(row, selection);
                  const formattedValue = value === null
                    ? "—"
                    : formatMetricValue(metric, value, row?.currency ?? "UNKNOWN");
                  return (
                    <div
                      className="comparison-workspace__chart-bar-item"
                      key={selectionKey(selection)}
                    >
                      <span
                        aria-label={value === null
                          ? `${label} ${metricLabel}: no data`
                          : undefined}
                        className="comparison-workspace__chart-value"
                      >
                        {formattedValue}
                      </span>
                      <span className="comparison-workspace__chart-bar-track">
                        {value === null ? null : (
                          <span
                            aria-label={`${label} ${metricLabel}: ${formattedValue}`}
                            aria-valuemax={maximum}
                            aria-valuemin={0}
                            aria-valuenow={value}
                            className="comparison-workspace__chart-bar"
                            role="meter"
                            style={{
                              "--comparison-bar-height": `${maximum <= 0
                                ? 0
                                : Math.max(3, (value / maximum) * 100)}%`,
                              "--comparison-series-color": COMPARISON_SERIES_COLORS[index],
                            } as CSSProperties}
                          />
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ComparisonMetricMatrix({
  response,
  rows,
  selections,
}: {
  readonly response: ComparisonResponse;
  readonly rows: readonly (VideoMetricRow | undefined)[];
  readonly selections: readonly VideoSelection[];
}) {
  return (
    <table aria-label="Comparison metrics" className="comparison-workspace__matrix">
      <caption className="visually-hidden">Metrics for the selected videos in URL order</caption>
      <colgroup>
        <col className="comparison-workspace__label-column" />
        {EMPTY_SLOTS.map((slot) => <col className="comparison-workspace__video-column" key={slot} />)}
      </colgroup>
      <thead className="visually-hidden">
        <tr>
          <th scope="col">Metric</th>
          {selections.map((selection, index) => (
            <th key={selectionKey(selection)} scope="col">{rowLabel(rows[index], selection)}</th>
          ))}
          {EMPTY_SLOTS.slice(0, MAXIMUM_VIDEO_SELECTIONS - selections.length).map((slot) => (
            <th key={`heading-empty-${slot}`} scope="col">Empty slot</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {COMPARISON_MATRIX_METRICS.map((metric) => (
          <ComparisonMetricRow
            key={metric}
            metric={metric}
            response={response}
            rows={rows}
            selections={selections}
          />
        ))}
        <tr>
          <th className="comparison-workspace__metric-label" scope="row">Duration</th>
          {selections.map((selection, index) => (
            <td key={selectionKey(selection)}>{formatDuration(rows[index]?.lengthSeconds ?? null) ?? <NoDataCell />}</td>
          ))}
          {EMPTY_SLOTS.slice(0, MAXIMUM_VIDEO_SELECTIONS - selections.length).map((slot) => (
            <td aria-hidden="true" key={`duration-empty-${slot}`} />
          ))}
        </tr>
      </tbody>
    </table>
  );
}

function ComparisonMetricRow({
  metric,
  response,
  rows,
  selections,
}: {
  readonly metric: ReportMetricId;
  readonly response: ComparisonResponse;
  readonly rows: readonly (VideoMetricRow | undefined)[];
  readonly selections: readonly VideoSelection[];
}) {
  const label = MetricRegistry[metric].label;
  const best = bestMetricValue(
    rows,
    metric,
    response.coverage.videoMetricThreshold,
  );
  return (
    <tr>
      <th className="comparison-workspace__metric-label" scope="row">{label}</th>
      {selections.map((selection, index) => {
        const row = rows[index];
        const value = comparableMetricValue(
          row,
          metric,
          response.coverage.videoMetricThreshold,
        );
        const labelForVideo = rowLabel(row, selection);
        const isBest = best !== null && value === best;
        return (
          <td key={selectionKey(selection)}>
            <span className="visually-hidden">{labelForVideo}: </span>
            {value === null
              ? <NoDataCell />
              : formatMetricValue(metric, value, row?.currency ?? "UNKNOWN")}
            {isBest ? (
              <span
                aria-label={`Best ${label}: ${labelForVideo}`}
                className="comparison-workspace__best"
                title={`Best ${label}`}
              >
                <span aria-hidden="true">●</span>
              </span>
            ) : null}
          </td>
        );
      })}
      {EMPTY_SLOTS.slice(0, MAXIMUM_VIDEO_SELECTIONS - selections.length).map((slot) => (
        <td aria-hidden="true" key={`metric-${metric}-empty-${slot}`} />
      ))}
    </tr>
  );
}
